// What deleting a lesson leaves behind, and what a delete is allowed to do
// about it.
//
// Taking attendance writes five documents keyed off the occurrence — the
// record, the charge, the make-up credit, the prepaid consume and the payment
// taken at the lesson (see data/useAttendance.jsx, which lists them). A delete
// that touched none of them would let a lesson marked Present and charged $30
// be removed from the calendar and leave a live $30 charge on the family's
// invoice with nothing on any screen connecting the two (#157).
//
// ── Refuse, cascade or reverse ─────────────────────────────────────────────
//
// **Reverse.** Against the other two:
//
//   * *Cascade* — delete the five documents with the lesson. A money document
//     is never deleted, because a delete does not converge: a device that was
//     offline for it still holds the charge in its cache and restores it (see
//     `isActive` in ledger.js, #70). It would also remove a *payment a family
//     actually handed over* without trace.
//
//   * *Refuse* — block the delete while any attendance record exists.
//     `unrecorded` is itself billable and the attendance drawer always writes a
//     status, so opening the drawer and saving would wedge a lesson for good;
//     one taught lesson would refuse a "this and following" tail; and the
//     remedy, `clearAttendance`, sets the status back to `unrecorded`, which
//     *restores* the charge and does not retract the payment.
//
//   * *Reverse* — retract what the attendance decision produced. The
//     compensating entry *is* the tombstone: `voided: true` beside `voidedAt`,
//     the same retraction `setAttendance` writes when a status flips to a
//     non-billable one. A negative "reversal" entry could not be made
//     idempotent — a replayed offline write would reverse twice.
//
// **Automatic**, as in MMS: deleting a lesson carrying a live charge removes
// the transaction row along with the event, silently, on both a future lesson
// and one already billed. So deleting a lesson always retracts its ledger
// entries, and there is no way to decline; no delete leaves a live charge
// whose lesson is gone (#193).
//
// The delete dialog does not go quiet to match MMS: RecurringScopeDialog shows
// what the lesson carried. It draws no checkbox; `removalSentences` below
// states what the delete does.
//
// **The cost:** a lesson that was taught, charged, paid and then tidied off the
// calendar loses its charge on delete, with no way to keep it.
//
// ── What is reversed, and what is not ──────────────────────────────────────
//
//   * **The charge** is voided. It is a projection of the attendance decision,
//     and the lesson it was projected from is gone.
//
//   * **The prepaid consume** is voided, which gives the lesson back to the
//     student. It has to move with the charge and cannot be left behind: the
//     two are mutually exclusive ways of paying for the same lesson
//     (`shouldConsumePackage`, core/src/packages.rs), so voiding one and not
//     the other would make a deleted lesson free for a family who pays cash
//     and costly for a family who prepaid. Voided rather than answered with a
//     `restore` entry, because `restore` would need a fresh id and would
//     restore twice if the write were replayed, while the consume's id is
//     derived and the tombstone is idempotent.
//
//   * **The make-up credit** is voided. The absence it was granted for is no
//     longer a lesson. (A credit *booked* against another lesson would be taken
//     back underneath that booking; nothing writes `book` entries (#363), so
//     that is unreachable rather than handled.)
//
//   * **The payment is never voided, by anything on this path.** Money handed
//     over is a fact, not a consequence of how the lesson was later filed — as
//     `clearAttendance` in useAttendance.jsx also holds. Voiding the charge and
//     keeping the payment leaves the family in credit by what they paid, and
//     the dialog says so (#157).
//
//   * **The attendance record itself is left alone.** It is not money, no fold
//     reads a tombstone in that collection, and it holds the only copy of the
//     three lesson notes. "The lesson was taught and the studio chose not to
//     bill for it" is the state this produces.
//
//   * **A fixed-monthly student's fee is untouched**: that charge is
//     `chgm_<student>__<month>`, one per month rather than one per lesson,
//     written by invoice generation and never rewritten
//     (functions/invoicing/monthlyFees.js, #787), so removing a lesson does
//     not change a fee already written. Before the fee is written, removing
//     the month's first billable lesson moves the fee to the next one that
//     bills, and a month whose lessons are all removed gets no fee.
//
// #188's Delete control on the family ledger — retracting a single
// hand-entered entry — is a separate path, gated to entries no lesson delete
// touches.
//
// Pure, and finding rather than searching: every document above has an id
// derived from the occurrence and the student (ids.js), so the attendance
// records in scope name the rest exactly. Nothing here scans by `sourceId`.

// ── One tombstone predicate, under two names (#214, #235) ──────────────────
//
// There is one rule, `core/src/tombstone.rs`, and it takes the **two fields**
// instead of a document — which is what lets it serve four collections whose
// shapes have nothing else in common. `ledgerActive` and `makeupActive` below
// are both re-exports of it, so the aliases are two names for one function.
//
// The aliases say *which collection each `pick` is reading* at the call site.
// The package consume is judged by `ledger.js`'s predicate, whose argument at
// `isActive` is about a *package* consume (#70).
//
// ── The rules are the core's ───────────────────────────────────────────────
//
// Every rule in this file is `core/src/removal.rs` — Delete's plan, the marked
// lessons, the footprint (tombstone included) and the sentences — so the
// phones' delete dialogs say and do the same. What stays is the adapter: the
// stored documents' loose fields coerced (`Number(amountMinor) || 0`, a truthy
// `voided`/`voidedAt`), and the core's positions and ids turned back into the
// documents and objects these functions return. `core/src/calendar_vectors.json`
// holds the recorded answers.
import { coreSync } from './core.js';
import { eventOccurrenceInput, seriesRuleInput } from './eventSeries.js';

// ── What Delete does to the schedule, for each scope ───────────────────────
//
// The reasons for each branch are in `CalendarPage.jsx`, beside the write it
// chooses.
//
// `occurrence` is the one the popover was opened on, `savedRule` its series as
// stored (or null), and `scope` the dialog's radio — `one` or `following`.
//
//   recurring               whether the dialog asks a scope at all: a one-time
//                           event has one entry, and only who to tell
//   cut                     the rule date "this and following" cuts at — a
//                           moved lesson is still produced by its rule date
//   followingIsWholeSeries  "this and following" from the rule's own start
//   followingFrom           the date the following scope's marked lessons are
//                           counted from; null for the whole series
//   action                  `deleteSeries`, `removeOccurrence`, or `endSeries`
//   dropOverridesFrom       the overrides deleted first, from this date on
//   endDate                 the rule's new end, for `endSeries`
export function planEventDelete({ occurrence, savedRule = null, scope = 'one' } = {}) {
  const p = coreSync().planEventDelete(
    eventOccurrenceInput(occurrence), seriesRuleInput(savedRule), String(scope),
  );
  return {
    recurring: p.recurring,
    cut: p.cut ?? null,
    followingIsWholeSeries: p.followingIsWholeSeries,
    followingFrom: p.followingFrom ?? null,
    action: p.action,
    dropOverridesFrom: p.dropOverridesFrom ?? null,
    endDate: p.endDate ?? null,
  };
}

const marks = (records) => (records || []).map((r) => ({
  occurrenceId: String(r?.occurrenceId ?? ''),
  studentId: r?.studentId ? String(r.studentId) : null,
}));

// The attendance records a delete would strand.
//
// `fromDate` is the "this and following" cut, on the *rule* date, matching the
// cut CalendarPage hands `dropOverridesFrom`. Omit it and the whole series is
// in scope, which is what deleting a series or a one-time event means.
//
// Bounded by what exists rather than by the schedule: a series that repeats
// indefinitely has no finite occurrence list, but it has a finite number of
// attendance records, and those are the only occurrences that can have produced
// money.
export function markedOccurrences(records, { seriesId, fromDate = null } = {}) {
  const list = records || [];
  return coreSync().markedOccurrences(marks(list), String(seriesId), fromDate || null)
    .map((i) => list[i]);
}

// Just the one occurrence, for Delete → "This event".
export function markedOn(records, occurrenceId) {
  const list = records || [];
  return coreSync().markedOn(marks(list), String(occurrenceId)).map((i) => list[i]);
}

// A stored money document as the core reads it — the tombstone's two fields
// read as `isActive` reads them, as truthiness.
const entries = (docs) => (docs || []).map((d) => ({
  id: String(d?.id ?? ''),
  familyId: d?.familyId == null ? null : String(d.familyId),
  // `Math.round` is the rounding the core's `sum` gave each amount.
  amountMinor: Math.round(Number(d?.amountMinor) || 0),
  voided: Boolean(d?.voided),
  voidedAt: d?.voidedAt ? 'set' : null,
}));

// The core's footprint travels on the one this returns, unenumerated, so
// `retractionTargets` and `hasRetraction` read its answer rather than
// re-deriving it; the enumerable fields are the documents.
const CORE = Symbol('removalFootprint.core');

// What the marked lessons produced, as the live documents themselves — ids
// included, because the writer voids exactly these and nothing wider.
//
// Only documents this device can actually see are reported. A tombstone is
// written as a merge, and a merge against a document that does not exist is a
// *create*: a bare `{ voided, voidedAt }` create has no kind, no amount and no
// family, the rules reject it, and because the retraction is one batch the
// whole delete would do nothing (as `setAttendance` also notes).
//
// Make-up credits a lesson *spent* (#303) are a fourth bucket and not a fifth
// reading of `credits`: they retract through the same collection and the same
// tombstone, but they read the opposite way to a teacher — a delete **gives one
// back** — so the sentences need them apart.
export function removalFootprint({
  records = [], ledgerEntries = [], packageEntries = [], makeupEntries = [],
} = {}) {
  const f = coreSync().removalFootprint(
    marks(records), entries(ledgerEntries), entries(packageEntries), entries(makeupEntries),
  );
  // The core found them by id; the documents are the caller's, the last of an
  // id winning as a `Map` would.
  const byId = (docs) => {
    const m = new Map();
    for (const d of docs || []) m.set(d?.id, d);
    return (ids) => ids.map((id) => m.get(id));
  };
  const ledger = byId(ledgerEntries);
  const makeups = byId(makeupEntries);
  const footprint = {
    charges: ledger(f.chargeIds),
    payments: ledger(f.paymentIds),
    consumes: byId(packageEntries)(f.consumeIds),
    credits: makeups(f.creditIds),
    spentCredits: makeups(f.spentCreditIds),
    chargeMinor: Number(f.chargeMinor),
    paymentMinor: Number(f.paymentMinor),
    familyCount: f.familyCount,
    lessonCount: f.lessonCount,
    // True when there is nothing to say. The dialog shows its money block only
    // when this is false, so an ordinary delete of an unmarked lesson shows
    // none.
    isEmpty: f.isEmpty,
  };
  Object.defineProperty(footprint, CORE, { value: f });
  return footprint;
}

// The core's footprint behind one of ours, or — for a footprint written out by
// hand — the same record built from its buckets.
function coreFootprint(footprint) {
  if (footprint?.[CORE]) return footprint[CORE];
  const ids = (list) => (list || []).map((e) => String(e?.id ?? ''));
  return {
    chargeIds: ids(footprint.charges),
    paymentIds: ids(footprint.payments),
    consumeIds: ids(footprint.consumes),
    creditIds: ids(footprint.credits),
    spentCreditIds: ids(footprint.spentCredits),
    makeupIds: [...ids(footprint.credits), ...ids(footprint.spentCredits)],
    chargeMinor: Number(footprint.chargeMinor) || 0,
    paymentMinor: Number(footprint.paymentMinor) || 0,
    familyCount: Number(footprint.familyCount) || 0,
    lessonCount: Number(footprint.lessonCount) || 0,
    isEmpty: Boolean(footprint.isEmpty),
    hasRetraction: false,
  };
}

// The ids the retraction writes to, grouped by collection. Payments are absent
// by construction and not by omission — see the header. Both make-up buckets
// go to `makeupIds`, because they are one collection and
// `voidAttendanceEffects` writes the same tombstone to each; they are two
// buckets on the footprint because the *sentences* need them apart.
export function retractionTargets(footprint) {
  const f = coreFootprint(footprint);
  return { ledgerIds: f.chargeIds, packageIds: f.consumeIds, makeupIds: f.makeupIds };
}

// Whether the delete writes a retraction at all.
export function hasRetraction(footprint) {
  return Boolean(footprint?.[CORE]?.hasRetraction);
}

// The sentences the delete dialog reads out.
//
// Here rather than in the dialog so they can be tested without a DOM, as
// `noteCounterText` in lessonNotes.js is. `formatMoney` is passed in because
// the studio's currency and locale live in React context.
//
// Two strings: `summary` states what the marked lessons carry; `consequence`
// states what deleting them does about it, unconditionally (there is no
// checkbox). When the lessons carry a payment and nothing else, it says the
// money stays; a payment is named in full, because the family ends up *in
// credit*.
export function removalSentences(footprint, formatMoney) {
  if (!footprint || footprint.isEmpty) return null;
  const f = coreFootprint(footprint);
  const s = coreSync().removalSentences(
    f, formatMoney(Number(f.chargeMinor)), formatMoney(Number(f.paymentMinor)),
  );
  return s ? { summary: s.summary, consequence: s.consequence } : null;
}
